import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AuthLayout, AuthAlert } from './AuthLayout';
import { Button } from '@/components/ui/button';
import { useSession } from '@/features/session/useSession';
import { useWorkspaceStore } from '@/stores/workspace';
import { useAcceptInvite, useInvitePreview } from './api-invite';
import { apiErrorMessage } from '@/lib/api-errors';

export function AcceptInvitePage() {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { session, loading } = useSession();
  const navigate = useNavigate();
  const { setActiveWorkspaceId } = useWorkspaceStore();
  const acceptInvite = useAcceptInvite();
  const preview = useInvitePreview(token, !loading && !session);

  const statusMessages: Record<string, string> = {
    not_found: t('acceptInvite.statusMessages.notFound'),
    expired: t('acceptInvite.statusMessages.expired'),
    revoked: t('acceptInvite.statusMessages.revoked'),
    accepted: t('acceptInvite.statusMessages.accepted'),
  };

  if (loading) return null;
  if (!token) {
    return (
      <AuthLayout title={t('acceptInvite.invalidTitle')}>
        <AuthAlert>{t('acceptInvite.missingToken')}</AuthAlert>
      </AuthLayout>
    );
  }

  if (!session) {
    if (preview.isLoading) return null;

    if (preview.data && preview.data.status !== 'valid') {
      return (
        <AuthLayout title={t('acceptInvite.unavailableTitle')}>
          <AuthAlert>{statusMessages[preview.data.status] ?? t('acceptInvite.statusMessages.fallback')}</AuthAlert>
        </AuthLayout>
      );
    }

    const next = encodeURIComponent(`/accept-invite?token=${token}`);
    const email = preview.data?.email ?? null;
    const emailParam = email ? `&email=${encodeURIComponent(email)}` : '';
    const signupHref = `/signup?next=${next}${emailParam}`;
    const loginHref = `/login?next=${next}${emailParam}`;

    return (
      <AuthLayout
        title={t('acceptInvite.joinTitle')}
        subtitle={
          preview.data
            ? t('acceptInvite.subtitleWithDetails', { workspaceName: preview.data.workspaceName, role: preview.data.role })
            : t('acceptInvite.subtitleGeneric')
        }
      >
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate(signupHref)} className="w-full">
            {t('acceptInvite.createPasswordCta')}
          </Button>
          <p className="text-center text-sm text-muted">
            {t('acceptInvite.alreadyHaveAccount')}{' '}
            <Link to={loginHref} className="font-semibold text-accent-ink hover:underline">
              {t('acceptInvite.signIn')}
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  async function handleAccept() {
    const result = await acceptInvite.mutateAsync(token);
    setActiveWorkspaceId(session!.user.id, result.workspaceId);
    navigate('/', { replace: true });
  }

  return (
    <AuthLayout title={t('acceptInvite.joinTitle')} subtitle={t('acceptInvite.subtitleGeneric')}>
      <div className="flex flex-col gap-4">
        {acceptInvite.isError ? (
          <AuthAlert>{apiErrorMessage(acceptInvite.error)}</AuthAlert>
        ) : null}
        <Button onClick={handleAccept} loading={acceptInvite.isPending} className="w-full">
          {t('acceptInvite.acceptCta')}
        </Button>
      </div>
    </AuthLayout>
  );
}
